import { Button, Stack, type SxProps, type Theme } from "@mui/material";
import { useMemo, useState } from "react";
import type { Match } from "../../api/AlqemiDataService";
import {
  perelynBlack,
  perelynPrimary,
  smallGap,
  tinyGap,
} from "../../styles/consts";
import Candidate from "./Candidate";

const isActive = (m: Match): boolean => m.consultant_active !== false;

type CandidateSelectionRowProps = {
  matches: Match[] | undefined;
  displayedMatchEmail: string;
  onCandidateClick: (email: string) => void;
  disabled?: boolean;
  showDetailedScores?: boolean;
  /**
   * Extra sx applied to the outer Stack. Useful for `{ flex: 1, minWidth: 0 }`
   * when this row sits inside a horizontal flex with sibling controls.
   */
  sx?: SxProps<Theme>;
};

/**
 * Shows top N candidates plus the currently selected one when outside the top N;
 * expand scrolls horizontally within this row only so the page stays static.
 */
export default function CandidateSelectionRow({
  matches,
  displayedMatchEmail,
  onCandidateClick,
  disabled = false,
  showDetailedScores = false,
  sx,
}: CandidateSelectionRowProps) {
  const [showAll, setShowAll] = useState(false);

  const sortedMatches = useMemo(
    () => [...(matches ?? [])].sort((a, b) => (b.score ?? 0) - (a.score ?? 0)),
    [matches],
  );

  const activeMatches = useMemo(
    () => sortedMatches.filter(isActive),
    [sortedMatches],
  );
  const inactiveMatches = useMemo(
    () => sortedMatches.filter((m) => !isActive(m)),
    [sortedMatches],
  );

  const visibleMatches = useMemo(() => {
    if (showAll) return [...activeMatches, ...inactiveMatches];
    if (
      displayedMatchEmail &&
      !activeMatches.some((m) => m.candidate?.email === displayedMatchEmail)
    ) {
      const displayed = sortedMatches.find(
        (m) => m.candidate?.email === displayedMatchEmail,
      );
      if (displayed) return [...activeMatches, displayed];
    }
    return activeMatches;
  }, [
    activeMatches,
    inactiveMatches,
    sortedMatches,
    showAll,
    displayedMatchEmail,
  ]);

  const hasMoreToReveal = inactiveMatches.length > 0;

  if (sortedMatches.length === 0) return null;

  return (
    <Stack
      sx={[
        {
          flexDirection: "row",
          alignItems: "flex-start",
          gap: smallGap,
          minWidth: 0,
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <Stack
        sx={{
          flexDirection: "row",
          gap: smallGap,
          overflowX: showAll ? "auto" : "hidden",
          flex: 1,
          minWidth: 0,
          scrollbarGutter: "stable",
          paddingBottom: tinyGap,
        }}
      >
        {visibleMatches.map((match, idx) => (
          <Candidate
            key={match.id ?? match.candidate?.email ?? idx}
            match={match}
            isDisplayed={match.candidate?.email === displayedMatchEmail}
            isInactive={!isActive(match)}
            onClick={() => {
              if (disabled) return;
              onCandidateClick(match.candidate?.email || "");
            }}
            disabled={disabled}
            showDetailedScores={showDetailedScores}
          />
        ))}
      </Stack>
      {(hasMoreToReveal || showAll) && (
        <Button
          variant="outlined"
          size="small"
          onClick={() => setShowAll((prev) => !prev)}
          sx={{
            whiteSpace: "nowrap",
            flexShrink: 0,
            alignSelf: "flex-start",
            borderColor: perelynPrimary,
            color: perelynBlack,
            "&:hover": {
              borderColor: perelynBlack,
              backgroundColor: perelynPrimary,
            },
          }}
        >
          {showAll ? "Show fewer" : `Show all (${sortedMatches.length})`}
        </Button>
      )}
    </Stack>
  );
}
